import { GeneralSpinner } from '@Components/generalSpinner';
import { Spinner } from '@lili/lili-frontend-common';
import { AddOwner } from '@Pages/happyFlowPages/addOwner/AddOwner';
import { addOwnerSelectionQueryParamsSchema } from '@Pages/happyFlowPages/addOwner/addOwnerSelectionQueryParamsSchema';
import {
  getBusinessOwnerData,
  getBusinessOwners,
} from '@Pages/happyFlowPages/ownersCenter/api/ownersCenter.api';
import { OwnersCenter } from '@Pages/happyFlowPages/ownersCenter/OwnersCenter';
import { beforeLoadGeneral } from '@Router/middleware';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { useBoundStore } from '@Store/useBoundStore';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createRoute, useSearch } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Suspense } from 'react';
import { rootRoute } from './rootRoute';
import { getBusinessOwner } from '@Services/api/businessOwner';

const getBusinessOwnerQueryData = {
  queryKey: (id?: string) => ['getBusinessOwnerQueryData', id],
  queryFn: (id?: string) => () => (id ? getBusinessOwner(id) : null),
};
const AddOwnerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.ADD_OWNER].path,
  beforeLoad: () => {
    beforeLoadGeneral({ page: RoutesMap.ADD_OWNER, shouldUpdateServerNavStack: false });
  },
  validateSearch: zodSearchValidator(addOwnerSelectionQueryParamsSchema),
  loader: async ({ context, location: { search } }) => {
    const res = addOwnerSelectionQueryParamsSchema.safeParse(search);
    if (!res.data?.uboId) return;
    const querOpts = {
      queryKey: getBusinessOwnerQueryData.queryKey(res.data.uboId),
      queryFn: getBusinessOwnerQueryData.queryFn(res.data.uboId),
    };
    await context.queryClient.ensureQueryData(querOpts);
  },
  component: function AddOwnerComponent() {
    const search = useSearch({
      from: routesModel[RoutesMap.ADD_OWNER].path,
    });
    const res = addOwnerSelectionQueryParamsSchema.safeParse(search);
    const querOpts = {
      queryKey: getBusinessOwnerQueryData.queryKey(res.data?.uboId),
      queryFn: getBusinessOwnerQueryData.queryFn(res.data?.uboId),
    };
    const { data: businessOwner } = useSuspenseQuery(querOpts);
    return (
      <Suspense fallback={<GeneralSpinner />}>
        <AddOwner ubo={businessOwner!} />
      </Suspense>
    );
  },
});

const getBusinessOwnersQueryData = {
  queryKey: ['getBusinessOwners'],
  queryFn: () =>
    !getBusinessOwnerData().businessExternalId || !getBusinessOwnerData().customerId
      ? null
      : getBusinessOwners(),
};

export const ownerCenterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.OWNER_CENTER].path,
  beforeLoad: () => {
    beforeLoadGeneral({ page: RoutesMap.OWNER_CENTER, shouldUpdateServerNavStack: true });
    return {
      getBusinessOwnersQueryData,
    };
  },
  loader: async ({ context }) => {
    const businessOwners = await context.queryClient.ensureQueryData(getBusinessOwnersQueryData);
    const { getCustomerData, setCustomerData } = useBoundStore.getState();
    const customerToUpdate = { ...getCustomerData() };
    customerToUpdate.customerData.customerBusinessData.businessUbos = {
      uboResponseList: businessOwners || [],
    };

    setCustomerData(customerToUpdate);
    return businessOwners;
  },
  component: function OwnerCenterComponent() {
    const { data: businessOwners, refetch: refetchBusinessOwners } = useSuspenseQuery(
      getBusinessOwnersQueryData
    );
    const { getCustomerData, setCustomerData } = useBoundStore.getState();
    const customerToUpdate = { ...getCustomerData() };
    customerToUpdate.customerData.customerBusinessData.businessUbos = {
      uboResponseList: businessOwners || [],
    };
    setCustomerData(customerToUpdate);

    return (
      <Suspense fallback={<Spinner width='32px' height='32px' />}>
        <OwnersCenter
          businessOwners={businessOwners || []}
          refetchBusinessOwners={refetchBusinessOwners}
        />
      </Suspense>
    );
  },
});

export const ownerRoutes = [AddOwnerRoute, ownerCenterRoute];
